import { useCallback, useEffect, useRef, useState } from "react";
import { CSearchInput } from "../../components/CSearchInput";
import NavBar from "../../components/NavBar";
import BaseScreen from "../BaseScreen";
import { MainNavProps } from "../navigation/main/MainParamList";
import { FlatList } from "react-native-gesture-handler";
import {
  Platform,
  Pressable,
  SafeAreaView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import MoreIcon from "../../assets/svg/icon_more.svg";
import AddIcon from "../../assets/svg/icon_add.svg";
import { User } from "../../../imsdk/db/t_user";
import {
  color_333333,
  color_999999,
  color_BEBEBE,
  color_EDEEF1,
  color_EEF3FA,
  color_666666,
  color_main,
  ColorGrey,
  ColorLine,
  white,
} from "../../assets/style/colors";
import { hideLoading, showLoading } from "../../components/Loading";
import { getUserName } from "../Chat/chat-helper";
import { showToast } from "../../components/Toast";
// 保持为本文件实际使用的 hooks/工具
import { ButtonView } from "../../components/ButtonView";
import { AddLabelModal } from "./ContactsLabelScreenModal";
// getScreenHeight 已迁移或不在此文件中使用，已移除
import {
  Str_Add,
  Str_ConstactLabel,
  Str_CreateFail,
  Str_CreateSucc,
  Str_LabelAlreadyExists,
  Str_LabelEmpty,

  Str_LabelNotNull,
} from "../../language/string_res";
import globalEventBus from "../../utils/EventBus";
import * as ImSdk from "../../../imsdk/api";
import { LabelGroupedData } from "../../../imsdk/db/t_label";
import { ListEmpty } from "../../components/ListEmpty";
import { useIsFocused } from "@react-navigation/native";

const limit = 300;

export type DelEvent = { label: string; uids: number[] };
export type AddEvent = { label: string; users: User[] };
export type UpdateLabelEvent = { oldLabel: string; newLabel: string };

export const ContactsLabelScreen = ({
  navigation,
}: MainNavProps<"ContactsLabelScreen">) => {
  const [search, setSearch] = useState("");
  const [searchEndText, setSearchEndText] = useState("");
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const [addModalVis, setAddModalVis] = useState(false);
  const [data, setData] = useState<LabelGroupedData[]>([]);
  const [loading, setLoading] = useState(false);
  const hasMore = useRef(true);
  const isFocused = useIsFocused();
  useEffect(() => {
    if (isFocused) {
      setData([]);
      fetchData(true);
    }
  }, [isFocused]);

  useEffect(() => {
    globalEventBus.on("DeleteLabel", handleDeleteLabel);
    globalEventBus.on("DelLabelUser", handleDelUser);
    globalEventBus.on("AddLabelUser", handleAddUser);
    globalEventBus.on("UpdateLabel", handleUpdateLabel);
    return () => {
      globalEventBus.off("DeleteLabel", handleDeleteLabel);
      globalEventBus.off("DelLabelUser", handleDelUser);
      globalEventBus.off("AddLabelUser", handleAddUser);
      globalEventBus.off("UpdateLabel", handleUpdateLabel);
    };
  }, []);

  const handleDeleteLabel = (label: string) => {
    setData((prevData) => {
      return prevData?.filter((v) => label !== v.label);
    });
  };
  const handleUpdateLabel = (event: UpdateLabelEvent) => {
    const { oldLabel, newLabel } = event;
    setData((prevData) => {
      return prevData?.map((v) => {
        if (v.label === oldLabel) {
          v.label = newLabel;
        }
        return v;
      });
    });
  };

  const handleDelUser = (event: DelEvent) => {
    const { label, uids } = event;
    setData((prevData) => {
      return prevData?.map((v) => {
        if (label === v.label) {
          return {
            ...v,
            users: v.users.filter((user) => !uids.includes(user.uid)),
          };
        }
        return v;
      });
    });
  };

  const handleAddUser = (event: AddEvent) => {
    const { label, users } = event;
    setData((prevData) => {
      return prevData?.map((v) => {
        if (label === v.label) {
          v.users.push(...users);
        }
        return v;
      });
    });
  };

  function fetchData(loading: boolean, text?: string) {
    // showLoading()
    if (loading) {
      setLoading(true);
    }
    ImSdk.getInstance()
      .GetLabels(0, limit, text)
      .then((r) => {
        hideLoading();
        setSearchEndText(text || "");
        if (r.labels && r.labels.length > 0) {
          let labels = r.labels;
          hasMore.current = limit === labels.length;
          setData((prevData) => {
            return [...prevData, ...labels];
          });
        } else {
          hasMore.current = false;
        }
      })
      .catch(() => {
        hideLoading();
        setLoading(false);
      })
      .finally(() => {
        hideLoading();
        setLoading(false);
      });
  }

  function loadMore() {
    if ((data ?? []).length === 0 || !hasMore.current || loading) {
      return;
    }
    fetchData(false, search);
  }

  function addLabel(label: string) {
    if (label.length === 0) {
      showToast(Str_LabelNotNull);
      return;
    }

    const index = data.findIndex((item) => item.label === label);
    if (index >= 0) {
      showToast(Str_LabelAlreadyExists);
      return;
    }

    showLoading();
    ImSdk.getInstance()
      .AddLabel(label, [], 1)
      .then((r) => {
        hideLoading();
        setTimeout(() => {
          if (!r.error) {
            showToast(Str_CreateSucc);
            setData((prevData) => {
              const list = [...(prevData ?? [])];
              list.push({ label: label, users: [] });
              return list;
            });
          } else {
            showToast(r.error || Str_CreateFail);
          }
        }, 300);
      });
  }

  const handleSearchChange = (text: string) => {
    setSearch(text);
    // 清除上一次的定时器
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    // 500ms 后执行
    debounceRef.current = setTimeout(() => {
      setData([]);
      fetchData(true, text);
    }, 500);
  };

  const itemClick = (item: { label: string; users?: User[] }) => {
    navigation.navigate("LabelDetailScreen", { label: item.label, users: item.users ?? [] });
  };

  const renderItem = ({
    item,
  }: {
    item: { label: string; users?: User[] };
  }) => {
    const users = item.users ?? [];
    const name = users
      .map((user, idx) => idx < 5 && getUserName(user))
      .join(", ");
    return (
      <Pressable onPress={() => itemClick(item)} style={styles.itemContainer}>
        <Text numberOfLines={1} ellipsizeMode={"tail"} style={styles.itemText}>
          {item.label}
          <Text style={[styles.itemSubText]}> ({users.length})</Text>
        </Text>
        {users.length > 0 && (
          <Text
            numberOfLines={1}
            ellipsizeMode={"tail"}
            style={styles.itemSubText}
          >
            {name}
          </Text>
        )}
      </Pressable>
    );
  };

  const handleCloseAdd = useCallback(() => {
    setAddModalVis(false);
  }, []);

  return (
    <BaseScreen>
      <NavBar
        title={Str_ConstactLabel}
        showBack={Platform.OS !== "web"}
        rightClick={() => setAddModalVis(true)}
        rightChildren={<AddIcon width={20} height={20} />}
      />
      {(data && data.length > 0) || setSearchEndText.length > 0 ? (
        <View style={{ flex: 1 }}>
          <CSearchInput searchVal={search} onChange={handleSearchChange} />
          <FlatList
            style={styles.listContainer}
            contentContainerStyle={styles.listContent}
            data={data}
            keyExtractor={(item, index) => index.toString()}
            ItemSeparatorComponent={() => <View style={styles.itemSeparator} />}
            onEndReached={loadMore}
            keyboardDismissMode="on-drag"
            onEndReachedThreshold={0.2}
            ListEmptyComponent={<ListEmpty />}
            renderItem={renderItem}
            showsVerticalScrollIndicator={false}
          />
          <SafeAreaView />
        </View>
      ) : (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>{Str_LabelEmpty}</Text>
          <ButtonView
            onPress={() => setAddModalVis(true)}
            style={styles.emptyContainerButton}
            textStyle={styles.emptyButton}
            text={Str_Add}
          />
        </View>
      )}

      <AddLabelModal
        visible={addModalVis}
        onClose={handleCloseAdd}
        addLabel={addLabel}
      />

      {/* 使用独立页面显示标签详情：导航到 ContactsLabelDetailScreen */}
    </BaseScreen>
  );
};
const styles = StyleSheet.create<any>({
  listContainer: {
    flex: 1,
    backgroundColor: '#F5F5F5',
  },
  listContent: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 20,
  },
  itemContainer: {
    backgroundColor: white,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  itemText: {
    color: color_333333,
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  itemSubText: {
    color: color_999999,
    fontSize: 14,
  },
  itemSeparator: {
    height: 12,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: white,
  },
  emptyText: {
    color: color_999999,
    fontSize: 16,
  },
  emptyContainerButton: {
    backgroundColor: ColorGrey,
    borderRadius: 8,
    paddingVertical: 12,
    marginTop: 20,
    paddingHorizontal: 70,
  },
  emptyButton: {
    color: color_333333,
    fontSize: 16,
    fontWeight: 'bold',
    fontFamily: '',
  },
});

